import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, ElementRef, Component, ChangeDetectionStrategy, ViewChild, Input, Directive, EventEmitter, Output, HostListener, NgModule } from '@angular/core';
import { of, Subject } from 'rxjs';
import { delay, tap, take, takeUntil } from 'rxjs/operators';
import { loadJs, guid } from '@firestitch/common';
import { CommonModule } from '@angular/common';
import { HttpClientJsonpModule } from '@angular/common/http';

function toAddress(address) {
    if (typeof (address) === 'string') {
        return address;
    }
    if (typeof address === 'object') {
        return [
            `${address.street || ''}`.trim(),
            `${address.city || ''}`.trim(),
            `${address.region || ''}`.trim(),
            `${address.zip || ''}`.trim(),
            `${address.country || ''}`.trim(),
        ]
            .filter((value) => !!value.length)
            .join(',');
    }
    return '';
}

const FS_MAP_GOOGLE_MAP_KEY = new InjectionToken('fs-map.google-map-key');

class FsMap {
    constructor(_googleMapKey) {
        this._googleMapKey = _googleMapKey;
        this._loaded = false;
    }
    get loaded$() {
        if (this._loaded) {
            return of(this._loaded);
        }
        if (!this._loaded$) {
            this._loaded$ = new Subject();
            loadJs(`https://maps.googleapis.com/maps/api/js?libraries=places,marker&loading=async&key=${this._googleMapKey}`)
                .pipe(delay(0))
                .subscribe(() => {
                this._loaded = true;
                this._loaded$.next();
                this._loaded$.complete();
            });
        }
        return this._loaded$.asObservable();
    }
}
FsMap.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMap, deps: [{ token: FS_MAP_GOOGLE_MAP_KEY }], target: i0.ɵɵFactoryTarget.Injectable });
FsMap.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMap, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMap, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_MAP_GOOGLE_MAP_KEY]
                }] }]; } });

class FsMapComponent {
    constructor(_map, _cdRef) {
        this._map = _map;
        this._cdRef = _cdRef;
        this.width = '100%';
        this.height = '400px';
        this.scrollwheel = true;
        this.fullscreenControl = false;
        this.streetViewControl = false;
        this.zoomControl = true;
        this.options = {};
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        this.loaded$
            .pipe(tap(() => this._init()))
            .subscribe();
    }
    mapInitialized() {
        setTimeout(() => {
            Object.keys(this.options?.events || [])
                .forEach((name) => {
                const eventName = name.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
                this.map.addListener(eventName, this.options.events[name]);
            });
        });
    }
    ngOnChanges(changes) {
        if (changes.address) {
            this._map.loaded$
                .pipe(take(1), takeUntil(this._destroy$))
                .subscribe(() => {
                const geocoder = new google.maps.Geocoder();
                geocoder.geocode({ 'address': toAddress(this.address) }, (results, status) => {
                    if (status == 'OK') {
                        const location = results[0]?.geometry?.location;
                        if (location) {
                            this.setCenter(location.lat(), location.lng());
                            this.addressMarker = {
                                position: location
                            };
                            this._cdRef.markForCheck();
                        }
                    }
                });
            });
        }
    }
    get loaded$() {
        return this._map.loaded$
            .pipe(takeUntil(this._destroy$));
    }
    setCenter(lat, lng) {
        this.lat = lat;
        this.lng = lng;
        this.map.setCenter(this.center);
    }
    get center() {
        return this.lat && this.lng ? new google.maps.LatLng(this.lat, this.lng) : null;
    }
    ngOnDestroy() {
        this._destroy$.next();
        this._destroy$.complete();
    }
    _init() {
        if (this.maxZoom) {
            this.options.maxZoom = this.maxZoom;
        }
        this.options = {
            mapId: guid(),
            ...this.options,
            scrollwheel: this.options.scrollwheel ?? this.scrollwheel,
            streetViewControl: this.options.streetViewControl ?? this.streetViewControl,
            zoomControl: this.options.zoomControl ?? this.zoomControl,
            zoom: this.options.zoom ?? this.zoom,
            fullscreenControl: this.options.fullscreenControl ?? this.fullscreenControl,
            mapTypeControlOptions: this.options.mapTypeControlOptions ?? this.mapTypeControlOptions,
        };
        if (this.center) {
            this.options.center = this.center;
        }
        this.map = new google.maps.Map(this.mapEl.nativeElement, this.options);
        this._initEvents();
    }
    _initEvents() {
        if (this.options.events) {
            [
                'boundsChanged',
                'centerChanged',
                'click',
                'contextmenu',
                'dblclick',
                'drag',
                'dragend',
                'dragstart',
                'headingChanged',
                'idle',
                'maptypeidChanged',
                'mousemove',
                'mouseout',
                'mouseover',
                'projectionChanged',
                'resize',
                'rightclick',
                'tilesloaded',
                'tiltChanged',
                'zoomChanged',
            ]
                .filter((name) => !!this.options.events[name])
                .forEach((name) => {
                var result = name.replace(/([A-Z])/g, " $1");
                const eventName = result.split(' ').join('_').toLowerCase();
                this.map.addListener(eventName, (event) => {
                    this.options.events[name](event);
                });
            });
        }
    }
}
FsMapComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapComponent, deps: [{ token: FsMap }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
FsMapComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "14.3.0", type: FsMapComponent, selector: "fs-map", inputs: { address: "address", width: "width", height: "height", lat: "lat", lng: "lng", scrollwheel: "scrollwheel", fullscreenControl: "fullscreenControl", streetViewControl: "streetViewControl", zoomControl: "zoomControl", maxZoom: "maxZoom", zoom: "zoom", mapTypeControlOptions: "mapTypeControlOptions", options: "options" }, viewQueries: [{ propertyName: "mapEl", first: true, predicate: ["mapEl"], descendants: true, read: ElementRef, static: true }], usesOnChanges: true, ngImport: i0, template: "\n<div \n  #mapEl \n  [style.height]=\"height\"\n  [style.width]=\"width\">\n</div>", styles: ["::ng-deep .fs-map-marker-container{outline:none}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapComponent, decorators: [{
            type: Component,
            args: [{ selector: 'fs-map', changeDetection: ChangeDetectionStrategy.OnPush, template: "\n<div \n  #mapEl \n  [style.height]=\"height\"\n  [style.width]=\"width\">\n</div>", styles: ["::ng-deep .fs-map-marker-container{outline:none}\n"] }]
        }], ctorParameters: function () { return [{ type: FsMap }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { mapEl: [{
                type: ViewChild,
                args: ['mapEl', { read: ElementRef, static: true }]
            }], address: [{
                type: Input
            }], width: [{
                type: Input
            }], height: [{
                type: Input
            }], lat: [{
                type: Input
            }], lng: [{
                type: Input
            }], scrollwheel: [{
                type: Input
            }], fullscreenControl: [{
                type: Input
            }], streetViewControl: [{
                type: Input
            }], zoomControl: [{
                type: Input
            }], maxZoom: [{
                type: Input
            }], zoom: [{
                type: Input
            }], mapTypeControlOptions: [{
                type: Input
            }], options: [{
                type: Input
            }] } });

class FsMapLinkDirective {
    constructor(_el) {
        this._el = _el;
        this.target = '_blank';
    }
    ngOnChanges(changes) {
        if (changes.address || changes.target) {
            const href = `https://www.google.com/maps/search/?api=1&query=${toAddress(this.address)}`;
            this._el.nativeElement.setAttribute('href', href);
            this._el.nativeElement.setAttribute('target', this.target);
        }
    }
}
FsMapLinkDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapLinkDirective, deps: [{ token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsMapLinkDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: FsMapLinkDirective, selector: "[fsMapLink]", inputs: { target: "target", address: "address" }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapLinkDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsMapLink]',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }]; }, propDecorators: { target: [{
                type: Input
            }], address: [{
                type: Input
            }] } });

class FsMapMarkerDirective {
    constructor(_el, _map, _mapService) {
        this._el = _el;
        this._map = _map;
        this._mapService = _mapService;
        this.options = {};
        this.zIndex = null;
        this.click = new EventEmitter();
        this.drag = new EventEmitter();
        this.dragStart = new EventEmitter();
        this.dragEnd = new EventEmitter();
        this._destroy$ = new Subject();
    }
    onClick(event) {
        if (event.domEvent) {
            event.domEvent.preventDefault();
            event.domEvent.stopPropagation();
        }
        else {
            event.preventDefault();
            event.stopPropagation();
        }
    }
    ngOnChanges(changes) {
        if ((changes.lat && !changes.lat.firstChange) ||
            (changes.lng && !changes.lng.firstChange)) {
            this._createMarker();
        }
    }
    ngAfterContentInit() {
        this._mapService.loaded$
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._createMarker();
        });
    }
    ngOnDestroy() {
        this.advancedMarkerElement?.remove();
        this._destroy$.next();
        this._destroy$.complete();
    }
    get position() {
        return new google.maps.LatLng(this.lat, this.lng);
    }
    _createMarker() {
        const content = this._el.nativeElement.innerHTML ? this._el.nativeElement : null;
        if (this.advancedMarkerElement) {
            this.advancedMarkerElement.content = content;
            this.advancedMarkerElement.position = {
                lat: this.lat,
                lng: this.lng,
            };
            return;
        }
        this.advancedMarkerElement = new google.maps.marker.AdvancedMarkerElement({
            ...this.options,
            map: this._map.map,
            content: content,
            zIndex: this.zIndex,
            position: {
                lat: this.lat,
                lng: this.lng,
            },
        });
        this.advancedMarkerElement.element
            .classList.add('fs-map-marker-container');
        if (this.click.observers.length) {
            this.advancedMarkerElement.addListener('click', (event) => {
                this.click.emit(event);
            });
        }
        if (this.drag.observers.length) {
            this.advancedMarkerElement.addListener('drag', (event) => {
                this.drag.emit(event);
            });
        }
        if (this.dragStart.observers.length) {
            this.advancedMarkerElement.addListener('dragstart', (event) => {
                this.dragStart.emit(event);
            });
        }
        if (this.dragEnd.observers.length) {
            this.advancedMarkerElement.addListener('dragend', (event) => {
                this.dragEnd.emit(event);
            });
        }
    }
}
FsMapMarkerDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapMarkerDirective, deps: [{ token: i0.ElementRef }, { token: FsMapComponent }, { token: FsMap }], target: i0.ɵɵFactoryTarget.Directive });
FsMapMarkerDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.3.0", type: FsMapMarkerDirective, selector: "fs-map-marker", inputs: { options: "options", lat: "lat", lng: "lng", zIndex: "zIndex" }, outputs: { click: "click", drag: "drag", dragStart: "dragStart", dragEnd: "dragEnd" }, host: { listeners: { "click": "onClick($event)" } }, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapMarkerDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: 'fs-map-marker',
                }]
        }], ctorParameters: function () { return [{ type: i0.ElementRef }, { type: FsMapComponent }, { type: FsMap }]; }, propDecorators: { options: [{
                type: Input
            }], lat: [{
                type: Input
            }], lng: [{
                type: Input
            }], zIndex: [{
                type: Input
            }], click: [{
                type: Output
            }], drag: [{
                type: Output
            }], dragStart: [{
                type: Output
            }], dragEnd: [{
                type: Output
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

class FsMapModule {
}
FsMapModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsMapModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.3.0", ngImport: i0, type: FsMapModule, declarations: [FsMapComponent,
        FsMapMarkerDirective,
        FsMapLinkDirective], imports: [CommonModule,
        //GoogleMapsModule,
        HttpClientJsonpModule], exports: [FsMapComponent,
        FsMapMarkerDirective,
        FsMapLinkDirective] });
FsMapModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapModule, imports: [CommonModule,
        //GoogleMapsModule,
        HttpClientJsonpModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.3.0", ngImport: i0, type: FsMapModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        //GoogleMapsModule,
                        HttpClientJsonpModule,
                    ],
                    declarations: [
                        FsMapComponent,
                        FsMapMarkerDirective,
                        FsMapLinkDirective,
                    ],
                    exports: [
                        FsMapComponent,
                        FsMapMarkerDirective,
                        FsMapLinkDirective,
                    ],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FS_MAP_GOOGLE_MAP_KEY, FsMap, FsMapComponent, FsMapLinkDirective, FsMapMarkerDirective, FsMapModule };
